import React, { useState, useEffect } from "react";
import { Form, Card, Button, Modal, Row, Spinner } from "react-bootstrap";
import { DataTable, Filter, Input } from "../../../../components";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getWallet, topUp, withdrawal, ownTransfer } from "../../../../api/wallet";
import { useForm, FormProvider } from "react-hook-form";
import { TransactionType, WalletType } from "../../../../shared/enums";
import dayjs from "dayjs";
import { getTransaction } from "../../../../api/transaction";
import { DATETIME_FORMAT } from "../../../../shared/constants";
import { formatFilterToQuery } from "../../../../shared/utils";
import { cloneDeep } from "lodash";

const DriverWallet = ({ userId }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const [wallets, setWallets] = useState([]);
    const [show, setShow] = useState(false);
    const [showTransfer, setShowTransfer] = useState(false);
    const [isTopUp, setIsTopUp] = useState(false);
    const [selectedWallet, setSelectedWallet] = useState(false);
    const form = useForm();
    const [filter, setFilter] = useState({});
    const [pagination, setPagination] = useState({ page: 1, pageSize: 25 });
    const [totalPages, setTotalPage] = useState(0);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const columns = [
        {
            key: "createdAt",
            label: t("CREATED_AT"),
            render: (createdAt, data) => dayjs(createdAt).format(DATETIME_FORMAT),
        },
        { key: "type", label: t("TYPE") },
        { key: "currency", label: t("CURRENCY") },
        {
            key: "amount",
            label: t("AMOUNT"),
            render: (amount, data) => amount?.toFixed(2),
        },
        {
            key: "beforeBalance",
            label: t("BEFORE_BALANCE"),
            render: (beforeBalance, data) => beforeBalance?.toFixed(2),
        },
        { key: "status", label: t("STATUS") },
        { key: "fullName", label: t("FULL_NAME") },
        {
            key: "phoneNumber",
            label: t("PHONE_NUMBER"),
            render: (phoneNumber, data) => data.countryCode + data.phoneNumber,
        },
        { key: "email", label: t("EMAIL") },
        { key: "refNo", label: t("REFERENCE_NO") },
    ];

    const filters = [
        { type: "datetime", label: t("FORM_DATE"), name: "fromDate" },
        { type: "datetime", label: t("TO_DATE"), name: "toDate" },
        {
            type: "select",
            label: t("TYPE"),
            name: "type",
            options: [{ label: "All", value: "" }, ...Object.keys(TransactionType).map((key) => ({
                label: t(key),
                value: TransactionType[key],
            }))],
        },
    ];

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        getTransactionList(pagination, filter);
    }, [pagination, filter]);

    useEffect(() => {
        form.reset();
    }, [show, showTransfer]);

    const getData = async () => {
        const response = await getWallet(userId);
        setWallets(response);
    };

    const getTransactionList = async (pagination, filter) => {
        setLoading(true);
        const response = await getTransaction({
            skip: (pagination?.page - 1) * pagination?.pageSize,
            limit: pagination?.pageSize,
            user: userId,
            ...formatFilterToQuery(filter, [{ keys: ["fromDate", "toDate"], formatter: (value) => dayjs(value).format() }]),
        });
        setData(response.items);
        setTotalPage(Math.ceil(response?.pagination?.total / pagination?.pageSize));
        setLoading(false);
    };

    const topup = async (data) => {
        const response = await topUp(selectedWallet.id, {
            amount: +data.amount,
            currency: selectedWallet.currency,
        });

        if (response.success) {
            setWallets((prev) =>
                prev.map((wallet) => {
                    if (wallet.id === selectedWallet.id) {
                        wallet.balance += +data.amount;
                    }
                    return wallet;
                })
            );
            getTransactionList(pagination, filter);
            setShow(false);
        }
    };

    const withdraw = async (data) => {
        const response = await withdrawal(selectedWallet.id, {
            amount: +data.amount,
            currency: selectedWallet.currency,
        });

        if (response.success) {
            setWallets((prev) =>
                prev.map((wallet) => {
                    if (wallet.id === selectedWallet.id) {
                        wallet.balance -= +data.amount;
                    }
                    return wallet;
                })
            );
            getTransactionList(pagination, filter);
            setShow(false);
        }
    };

    const transfer = async (data) => {
        const response = await ownTransfer({
            userId: selectedWallet.user,
            amount: +data.amount,
            currency: selectedWallet.currency,
        });

        if (response.success) {
            setWallets((prev) =>
                prev.map((wallet) => {
                    if (wallet.type === WalletType.CREDIT) {
                        wallet.balance += +data.amount;
                    } else {
                        wallet.balance -= +data.amount;
                    }
                    return wallet;
                })
            );
            getTransactionList(pagination, filter);
            setShowTransfer(false);
        }
    };

    return (
        <>
            <Row className="mb-3 mx-0">
                {wallets?.map((wallet) => (
                    <Card style={{ width: "18rem", marginRight: 12 }}>
                        <Card.Body>
                            <div className="d-flex justify-content-between">
                                <Card.Text className="mb-0">{wallet.currency}</Card.Text>
                                <Card.Text className="mb-0">
                                    {t(wallet.type.toUpperCase())}
                                </Card.Text>
                            </div>
                            <Card.Title as="h1">
                                {(wallet?.balance ?? 0).toFixed(2)}
                            </Card.Title>
                            {wallet.type === WalletType.CREDIT ? (
                                <Button
                                    variant={"success"}
                                    size={"sm"}
                                    className="me-3"
                                    onClick={() => {
                                        setShowTransfer(true);
                                        setSelectedWallet(wallet);
                                    }}
                                >
                                    {t("TRANSFER_FROM_DEBIT")}
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        variant={"success"}
                                        size={"sm"}
                                        className="me-3"
                                        onClick={() => {
                                            setShow(true);
                                            setIsTopUp(true);
                                            setSelectedWallet(wallet);
                                        }}
                                    >
                                        {t("TOPUP")}
                                    </Button>
                                    <Button
                                        variant={"danger"}
                                        size={"sm"}
                                        onClick={() => {
                                            setShow(true);
                                            setIsTopUp(false);
                                            setSelectedWallet(wallet);
                                        }}
                                    >
                                        {t("WITHDRAW")}
                                    </Button>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                ))}
            </Row>

            <Filter filters={filters} onChangeFilter={setFilter} />
            <DataTable
                columns={columns}
                data={data}
                pagination={{ ...pagination, totalPages, onChange: setPagination }}
                loading={loading}
            />

            <Modal show={show} onHide={() => setShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{isTopUp ? t("TOPUP") : t("WITHDRAW")}</Modal.Title>
                </Modal.Header>
                <FormProvider {...form}>
                    <Form onSubmit={form.handleSubmit(isTopUp ? topup : withdraw)}>
                        <Modal.Body>
                            <Input
                                label={t("AMOUNT")}
                                name={"amount"}
                                type="number"
                                formControlProps={{ step: "0.01" }}
                                rules={{ required: t("AMOUNT_IS_REQUIRED") }}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShow(false)}>
                                {t("CLOSE")}
                            </Button>
                            <Button variant="primary" type="submit">
                                {t("SAVE")}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </FormProvider>
            </Modal>

            <Modal show={showTransfer} onHide={() => setShowTransfer(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t("TRANSFER_FROM_DEBIT")}</Modal.Title>
                </Modal.Header>
                <FormProvider {...form}>
                    <Form onSubmit={form.handleSubmit(transfer)}>
                        <Modal.Body>
                            <Input
                                label={t("AMOUNT")}
                                name={"amount"}
                                type="number"
                                formControlProps={{ step: "0.01" }}
                                rules={{ required: t("AMOUNT_IS_REQUIRED") }}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => setShowTransfer(false)}
                            >
                                {t("CLOSE")}
                            </Button>
                            <Button variant="primary" type="submit">
                                {t("SAVE")}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </FormProvider>
            </Modal>
        </>
    );
};

export default DriverWallet;
